import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import EntityService from '../../services/EntityService';
import { API } from '../../shared/api/Api';

/**
 * Fetch all courses
 */
export const fetchCourses = createAsyncThunk(
  'courses/fetchCourses',
  async ({ token, params }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getAllEnties(
        token,
        API.Courses.app_courses_all,
        params
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return {
          items: response.data.data.items,
          currentPage: response.data.data.current_page,
          totalPages: response.data.data.last_page,
          total: response.data.data.total,
        };
      }
      return rejectWithValue(response.data.message || 'Failed to fetch courses');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch courses');
    }
  }
);

/**
 * Fetch a single course by ID
 */
export const fetchCourseById = createAsyncThunk(
  'courses/fetchCourseById',
  async ({ token, courseId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.getEntity(
        token,
        API.Courses.app_courses_get,
        courseId
      );
      if (response.data.status === 200 && response.data.errorCode === 0) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to fetch course');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch course');
    }
  }
);

/**
 * Create a new course
 */
export const createCourse = createAsyncThunk(
  'courses/createCourse',
  async ({ token, formData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (key === 'status') {
          data.append('status', value ? 'active' : 'disabled');
        } else if (key === 'requires_placement_test') {
          data.append('requires_placement_test', value ? '1' : '0');
        } else {
          data.append(key, value);
        }
      });

      const response = await EntityService.addEntity(
        token,
        API.Courses.app_courses_add,
        data
      );
      if (response.data.status === 200 || response.data.status === 201) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to create course');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create course';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Update an existing course
 */
export const updateCourse = createAsyncThunk(
  'courses/updateCourse',
  async ({ token, courseId, formData, initialData }, { rejectWithValue }) => {
    try {
      const data = new FormData();
      
      // Only append changed fields
      Object.keys(formData).forEach((key) => {
        const currentValue = formData[key];
        const initialValue = initialData[key];

        // Handle file inputs differently
        if (currentValue instanceof File) {
          data.append(key, currentValue);
        }
        // Handle status toggle
        else if (key === 'status') {
          if (currentValue !== initialValue) {
            data.append('status', currentValue ? 'active' : 'disabled');
          }
        }
        else if (key === 'requires_placement_test') {
          if (Boolean(currentValue) !== Boolean(initialValue)) {
            data.append('requires_placement_test', currentValue ? '1' : '0');
          }
        }
        // Handle other changed fields
        else if (currentValue !== initialValue) {
          data.append(key, currentValue);
        }
      });

      const response = await EntityService.updateEntity(
        token,
        API.Courses.app_courses_update,
        courseId,
        data
      );
      if (response.data.status === 200) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to update course');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update course';
      const validationErrors = error.response?.data?.data || {};
      return rejectWithValue({ message: errorMessage, errors: validationErrors });
    }
  }
);

/**
 * Delete a course
 */
export const deleteCourse = createAsyncThunk(
  'courses/deleteCourse',
  async ({ token, courseId }, { rejectWithValue }) => {
    try {
      const response = await EntityService.deleteEntity(
        token,
        API.Courses.app_courses_delete,
        courseId
      );
      if (response.data.status === 200) {
        return courseId;
      }
      return rejectWithValue(response.data.message || 'Failed to delete course');
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete course');
    }
  }
);

const initialState = {
  // List state
  entities: [],
  pagination: {
    currentPage: 1,
    totalPages: 1,
    total: 0,
  },
  
  // Selected entity state
  selectedEntity: null,
  initialFormData: null,
  
  // Status states
  listStatus: 'idle',
  detailStatus: 'idle',
  createStatus: 'idle',
  updateStatus: 'idle',
  deleteStatus: 'idle',
  
  // Error states
  listError: null,
  detailError: null,
  createError: null,
  updateError: null,
  deleteError: null,
  
  // Form validation errors
  formErrors: {},
};

const courseSlice = createSlice({
  name: 'courses',
  initialState,
  reducers: {
    clearSelectedEntity: (state) => {
      state.selectedEntity = null;
      state.initialFormData = null;
      state.detailStatus = 'idle';
      state.detailError = null;
      state.formErrors = {};
    },
    
    clearFormErrors: (state) => {
      state.formErrors = {};
      state.createError = null;
      state.updateError = null;
    },
    
    resetSubmissionStatus: (state) => {
      state.createStatus = 'idle';
      state.updateStatus = 'idle';
      state.createError = null;
      state.updateError = null;
      state.formErrors = {};
    },
    
    updateFormData: (state, action) => {
      if (state.selectedEntity) {
        state.selectedEntity = {
          ...state.selectedEntity,
          ...action.payload,
        };
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch courses list
    builder
      .addCase(fetchCourses.pending, (state) => {
        state.listStatus = 'loading';
        state.listError = null;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.listStatus = 'succeeded';
        state.entities = action.payload.items;
        state.pagination = {
          currentPage: action.payload.currentPage,
          totalPages: action.payload.totalPages,
          total: action.payload.total,
        };
        state.listError = null;
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.listError = action.payload;
      });

    // Fetch course by ID
    builder
      .addCase(fetchCourseById.pending, (state) => {
        state.detailStatus = 'loading';
        state.detailError = null;
      })
      .addCase(fetchCourseById.fulfilled, (state, action) => {
        state.detailStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.detailError = null;
        state.formErrors = {};
      })
      .addCase(fetchCourseById.rejected, (state, action) => {
        state.detailStatus = 'failed';
        state.detailError = action.payload;
      });

    // Create course
    builder
      .addCase(createCourse.pending, (state) => {
        state.createStatus = 'loading';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createCourse.fulfilled, (state) => {
        state.createStatus = 'succeeded';
        state.createError = null;
        state.formErrors = {};
      })
      .addCase(createCourse.rejected, (state, action) => {
        state.createStatus = 'failed';
        const errorData = action.payload;
        state.createError = errorData.message || 'Failed to create course';
        state.formErrors = errorData.errors || {};
      });

    // Update course
    builder
      .addCase(updateCourse.pending, (state) => {
        state.updateStatus = 'loading';
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updateCourse.fulfilled, (state, action) => {
        state.updateStatus = 'succeeded';
        state.selectedEntity = action.payload;
        state.initialFormData = { ...action.payload };
        state.updateError = null;
        state.formErrors = {};
      })
      .addCase(updateCourse.rejected, (state, action) => {
        state.updateStatus = 'failed';
        const errorData = action.payload;
        state.updateError = errorData.message || 'Failed to update course';
        state.formErrors = errorData.errors || {};
      });

    // Delete course
    builder
      .addCase(deleteCourse.pending, (state) => {
        state.deleteStatus = 'loading';
        state.deleteError = null;
      })
      .addCase(deleteCourse.fulfilled, (state, action) => {
        state.deleteStatus = 'succeeded';
        state.entities = state.entities.filter(course => course.id !== action.payload);
        state.deleteError = null;
      })
      .addCase(deleteCourse.rejected, (state, action) => {
        state.deleteStatus = 'failed';
        state.deleteError = action.payload;
      });
  },
});

export const {
  clearSelectedEntity,
  clearFormErrors,
  resetSubmissionStatus,
  updateFormData,
} = courseSlice.actions;

// Selectors
export const selectCourses = (state) => state.courses.entities;
export const selectCoursesPagination = (state) => state.courses.pagination;
export const selectSelectedCourse = (state) => state.courses.selectedEntity;
export const selectInitialFormData = (state) => state.courses.initialFormData;
export const selectCoursesListStatus = (state) => state.courses.listStatus;
export const selectCourseDetailStatus = (state) => state.courses.detailStatus;
export const selectCourseCreateStatus = (state) => state.courses.createStatus;
export const selectCourseUpdateStatus = (state) => state.courses.updateStatus;
export const selectCourseDeleteStatus = (state) => state.courses.deleteStatus;
export const selectCoursesListError = (state) => state.courses.listError;
export const selectCourseDetailError = (state) => state.courses.detailError;
export const selectCourseCreateError = (state) => state.courses.createError;
export const selectCourseUpdateError = (state) => state.courses.updateError;
export const selectCourseDeleteError = (state) => state.courses.deleteError;
export const selectCourseFormErrors = (state) => state.courses.formErrors;

export default courseSlice.reducer;
